import { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { getContracts } from '../../../lib/contracts';
import apiUtils from '../../../resources/api-utils';

/**
 * useSepoliaLive — read live protocol state from Sepolia.
 * Mirrors the strategy already used by App.js + apiUtils.getDappData()
 * (read-only JsonRpcProvider with INFURA_RPC, never window.ethereum).
 */

const FALLBACK = {
  blockNumber: 0,
  periodId: 0,
  ethRaisedTotal: 0,
  methMinted: 0,
  methDeltaPeriod: 0,
  auditorsActive: 0,
  auditorsTotal: 0,
  projectsCount: 0,
  tvl: 0,
  secondsToNextPeriod: 180,
  tickerEvents: [],
  isLive: false,
};

const REFRESH_MS = 30000;
const TICKER_MAX_ITEMS = 10;
const ZERO = '0x0000000000000000000000000000000000000000';
const INFURA_RPC = process.env.REACT_APP_INFURA_URL || '';

// Max periods to walk backward when paging contributions for the ticker.
// Each period that has contributions adds N RPC calls (one per contributor),
// so this caps RPC cost in degenerate cases.
const TICKER_PERIOD_CAP = 80;

function weiToEth(weiBn) {
  if (!weiBn) return 0;
  return parseFloat(ethers.utils.formatEther(weiBn));
}

function shortAddr(addr) {
  if (!addr || addr.length < 10) return addr || '0x?';
  return addr.slice(0, 6) + '…' + addr.slice(-4);
}

/** Format a single contribution returned by getContributorsPeriodIndex. */
function fmtContribution(data, period) {
  // data layout: [address, token_balance(wei), contribution(wei), timestamp, claimed]
  const addr = data[0];
  const eth = parseFloat(ethers.utils.formatEther(String(data[2] || 0)));
  const meth = parseFloat(ethers.utils.formatUnits(String(data[1] || 0), 18));
  const time = Number(data[3] || 0);
  const ethFmt = eth.toFixed(eth < 0.01 ? 4 : 3);
  const methFmt = meth.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return {
    period,
    timestamp: time,
    text: '<b>' + shortAddr(addr) + '</b> · contributed ' + ethFmt + ' ETH · received ' + methFmt + ' METH · period ' + period,
  };
}

/** Read the latest contributions directly from MethaCrowdsale, walking
 *  periods backwards. Same approach as metha-admin/utils/transactions.js
 *  (getNextPeriodWithContributions + getContributorsLengthPerPeriod +
 *  getContributorsPeriodIndex) — far more reliable than eth_getLogs. */
async function readRecentTransactions(crowdsale) {
  const items = [];
  let safety = 0;
  let period;
  try {
    // Find the latest period that actually has contributions.
    const latestBn = await crowdsale.getNextPeriodWithContributions(10000);
    period = Number(latestBn);
  } catch (e) {
    console.warn('[useSepoliaLive] getNextPeriodWithContributions failed:', e.message);
    return null;
  }
  if (!period || period < 1) return [];

  while (period >= 1 && items.length < TICKER_MAX_ITEMS && safety < TICKER_PERIOD_CAP) {
    safety++;
    let length = 0;
    try {
      const lenBn = await crowdsale.getContributorsLengthPerPeriod(period);
      length = Number(lenBn);
    } catch (e) {
      length = 0;
    }
    // Iterate contributions for this period, newest first
    for (let j = length - 1; j >= 0 && items.length < TICKER_MAX_ITEMS; j--) {
      try {
        const data = await crowdsale.getContributorsPeriodIndex(period, j);
        items.push(fmtContribution(data, period));
      } catch (e) { /* skip bad index */ }
    }
    if (items.length >= TICKER_MAX_ITEMS) break;
    // jump to the next period with contributions
    try {
      const nextBn = await crowdsale.getNextPeriodWithContributions(period);
      const next = Number(nextBn);
      if (!next || next === 0) break;
      period = next;
    } catch (e) {
      break;
    }
  }

  // Already in newest-first order because we walked backwards.
  return items.map(i => i.text);
}

async function readFundedProjects(auditingPool) {
  try {
    const totalBn = await auditingPool.getRequestCount();
    const total = totalBn.toNumber();
    if (total === 0) return 0;
    if (total > 100) return null;
    let funded = 0;
    for (let i = 0; i < total; i++) {
      try {
        const s = await auditingPool.getRequestStatus(i);
        const verdictCast     = !!(s.verdictCast     !== undefined ? s.verdictCast     : s[0]);
        const verdictAccepted = !!(s.verdictAccepted !== undefined ? s.verdictAccepted : s[1]);
        const cancelled       = !!(s.cancelled       !== undefined ? s.cancelled       : s[2]);
        if (verdictCast && verdictAccepted && !cancelled) funded++;
      } catch (e) { /* skip */ }
    }
    return funded;
  } catch (e) {
    console.warn('[useSepoliaLive] getRequestCount/Status failed:', e.message);
    return null;
  }
}

// v3: the legacy AuditingPool.getAuditorCount() entry point was removed
// in favor of the dedicated AuditorRegistryV2 contract. The landing page
// only needs the headcount, so we hit registry.firmCount() — every firm
// registered through registerFirm() is one auditor org. (Per-category
// breakdown lives in metha-admin-v2 / Auditors page.)
async function readAuditorsCount(registry) {
  try {
    const bn = await registry.firmCount();
    return bn.toNumber();
  } catch (e) {
    console.warn('[useSepoliaLive] registry.firmCount failed:', e.message);
    return null;
  }
}

export default function useSepoliaLive() {
  const [data, setData] = useState(FALLBACK);
  const aliveRef = useRef(true);

  useEffect(() => {
    const id = setInterval(() => {
      setData(d => ({
        ...d,
        secondsToNextPeriod: Math.max(0, d.secondsToNextPeriod - 1),
      }));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    aliveRef.current = true;

    if (!INFURA_RPC) {
      console.warn('[useSepoliaLive] REACT_APP_INFURA_URL not set, using mock data');
      return () => { aliveRef.current = false; };
    }

    const provider = new ethers.providers.JsonRpcProvider(INFURA_RPC);
    const { token, crowdsale, auditingPool, registry } = getContracts(provider);

    async function refresh() {
      let core = null;
      try {
        core = await apiUtils.getDappData();
      } catch (e) {
        console.warn('[useSepoliaLive] getDappData failed:', e.message);
      }

      let blockNumber = null;
      try { blockNumber = await provider.getBlockNumber(); }
      catch (e) { /* ignore */ }

      let tvl = null;
      try {
        const balBn = await crowdsale.getContractBalance();
        tvl = weiToEth(balBn);
      } catch (e) { /* ignore */ }

      let methPerPeriod = null;
      try {
        const tokensBn = await crowdsale.getTokensCreatedPerPeriod();
        methPerPeriod = parseFloat(ethers.utils.formatUnits(tokensBn, 18));
      } catch (e) { /* ignore */ }

      const projectsCount = await readFundedProjects(auditingPool);
      const auditorsTotal = await readAuditorsCount(registry);
      const tickerEvents  = await readRecentTransactions(crowdsale);

      if (!aliveRef.current) return;

      setData(d => {
        const next = { ...d };
        if (core) {
          next.isLive = true;
          next.periodId = Number(core.current_period);
          next.ethRaisedTotal = Number(core.wei_raised) || 0;
          next.methMinted = Number(core.token_supply) || 0;
          next.secondsToNextPeriod = Number(core.remaining_period_time) || 0;
        }
        if (blockNumber !== null)   next.blockNumber = blockNumber;
        if (tvl !== null)           next.tvl = tvl;
        if (methPerPeriod !== null) next.methDeltaPeriod = methPerPeriod;
        if (projectsCount !== null) next.projectsCount = projectsCount;
        if (auditorsTotal !== null) {
          next.auditorsTotal = auditorsTotal;
          next.auditorsActive = auditorsTotal;
        }
        if (tickerEvents !== null && tickerEvents.length > 0) {
          next.tickerEvents = tickerEvents;
        }
        return next;
      });
    }

    refresh();
    const id = setInterval(refresh, REFRESH_MS);

    return () => {
      aliveRef.current = false;
      clearInterval(id);
    };
  }, []);

  return data;
}

export { formatCountdown, formatBlock } from './useMockLiveData';
